import { z } from 'zod';

import { Id, IsoDateTime, Slug } from '../common/primitives';

// ── Enums ────────────────────────────────────────────────────────────────────

export const ProductKindSchema = z.enum(['PHYSICAL', 'DIGITAL']);
export type ProductKind = z.infer<typeof ProductKindSchema>;

export const AttributeKindSchema = z.enum(['TEXT', 'SELECT', 'NUMBER', 'BOOLEAN']);
export type AttributeKind = z.infer<typeof AttributeKindSchema>;

const HexColor = z
  .string()
  .trim()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Must be a hex colour, e.g. #67BED9');

// ── Brand ────────────────────────────────────────────────────────────────────

export const BrandSchema = z.object({
  id: Id,
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  logoFileId: z.string().nullable(),
  bannerFileId: z.string().nullable(),
  active: z.boolean(),
  productCount: z.number().int().nonnegative().optional(),
  createdAt: IsoDateTime,
});
export type Brand = z.infer<typeof BrandSchema>;

export const CreateBrandRequest = z
  .object({
    name: z.string().trim().min(2).max(120),
    slug: Slug,
    description: z.string().trim().max(2000).optional(),
    /// Despite the name, the logo's image URL: the apps load it as one.
    logoFileId: z.string().trim().max(2048).optional(),
    bannerFileId: z.string().trim().max(2048).optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type CreateBrandRequest = z.infer<typeof CreateBrandRequest>;

export const UpdateBrandRequest = CreateBrandRequest.partial()
  .extend({
    // null removes the logo or banner.
    logoFileId: z.string().trim().max(2048).nullable().optional(),
    bannerFileId: z.string().trim().max(2048).nullable().optional(),
  })
  .strict();
export type UpdateBrandRequest = z.infer<typeof UpdateBrandRequest>;

/// One row of the brand bulk-import payload.
export const BrandImportRow = z
  .object({
    name: z.string().trim().min(2).max(120),
    slug: Slug.optional(),
    description: z.string().trim().max(2000).optional(),
  })
  .strict();
export const BulkImportBrandsRequest = z
  .object({ brands: z.array(BrandImportRow).min(1).max(1000) })
  .strict();
export type BulkImportBrandsRequest = z.infer<typeof BulkImportBrandsRequest>;

export const BulkResultSchema = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  errors: z.array(z.string()),
});
export type BulkResult = z.infer<typeof BulkResultSchema>;

// ── Attribute ────────────────────────────────────────────────────────────────

export const AttributeValueSchema = z.object({
  id: Id,
  attributeId: Id,
  value: z.string(),
  position: z.number().int(),
  createdAt: IsoDateTime,
});
export type AttributeValue = z.infer<typeof AttributeValueSchema>;

export const AttributeSchema = z.object({
  id: Id,
  name: z.string(),
  slug: z.string(),
  kind: AttributeKindSchema,
  unit: z.string().nullable(),
  values: z.array(AttributeValueSchema),
  createdAt: IsoDateTime,
});
export type Attribute = z.infer<typeof AttributeSchema>;

export const CreateAttributeRequest = z
  .object({
    name: z.string().trim().min(2).max(120),
    slug: Slug,
    kind: AttributeKindSchema.optional(),
    unit: z.string().trim().max(20).optional(),
    values: z.array(z.string().trim().min(1).max(120)).max(200).optional(),
  })
  .strict();
export type CreateAttributeRequest = z.infer<typeof CreateAttributeRequest>;

export const UpdateAttributeRequest = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    kind: AttributeKindSchema.optional(),
    unit: z.string().trim().max(20).optional(),
  })
  .strict();
export type UpdateAttributeRequest = z.infer<typeof UpdateAttributeRequest>;

export const AddAttributeValueRequest = z
  .object({ value: z.string().trim().min(1).max(120) })
  .strict();
export type AddAttributeValueRequest = z.infer<typeof AddAttributeValueRequest>;

// ── Color ────────────────────────────────────────────────────────────────────

export const ColorSchema = z.object({
  id: Id,
  name: z.string(),
  hexCode: z.string(),
  active: z.boolean(),
  createdAt: IsoDateTime,
});
export type Color = z.infer<typeof ColorSchema>;

export const CreateColorRequest = z
  .object({
    name: z.string().trim().min(2).max(60),
    hexCode: HexColor,
    active: z.boolean().optional(),
  })
  .strict();
export type CreateColorRequest = z.infer<typeof CreateColorRequest>;

export const UpdateColorRequest = CreateColorRequest.partial().strict();
export type UpdateColorRequest = z.infer<typeof UpdateColorRequest>;

// ── Category discount ────────────────────────────────────────────────────────

export const CategoryDiscountSchema = z.object({
  id: Id,
  categoryId: Id,
  categoryName: z.string().optional(),
  label: z.string(),
  discountBps: z.number().int(),
  active: z.boolean(),
  startsAt: IsoDateTime,
  endsAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type CategoryDiscount = z.infer<typeof CategoryDiscountSchema>;

export const CreateCategoryDiscountRequest = z
  .object({
    categoryId: Id,
    label: z.string().trim().min(2).max(120),
    discountBps: z.number().int().min(0).max(10_000),
    active: z.boolean().optional(),
    startsAt: IsoDateTime.optional(),
    endsAt: IsoDateTime.optional(),
  })
  .strict();
export type CreateCategoryDiscountRequest = z.infer<typeof CreateCategoryDiscountRequest>;

export const UpdateCategoryDiscountRequest = z
  .object({
    label: z.string().trim().min(2).max(120).optional(),
    discountBps: z.number().int().min(0).max(10_000).optional(),
    active: z.boolean().optional(),
    endsAt: IsoDateTime.optional(),
  })
  .strict();
export type UpdateCategoryDiscountRequest = z.infer<typeof UpdateCategoryDiscountRequest>;

// ── Product bulk import / export ─────────────────────────────────────────────

/** A single product image already uploaded to Cloudinary (signed direct upload). */
export const ProductImportImage = z
  .object({
    fileId: z.string().trim().min(1),
    url: z.string().trim().url(),
    publicId: z.string().trim().max(300).optional(),
    alt: z.string().trim().max(200).optional(),
    isPrimary: z.boolean().optional(),
  })
  .strict();
export type ProductImportImage = z.infer<typeof ProductImportImage>;

export const ProductImportRow = z
  .object({
    title: z.string().trim().min(2).max(300),
    slug: Slug.optional(),
    categoryId: Id,
    brandId: Id.optional(),
    brand: z.string().trim().max(120).optional(),
    hsnSac: z
      .string()
      .trim()
      .regex(/^\d{4,8}$/),
    gstRate: z.number().int().optional(),
    productKind: ProductKindSchema.optional(),
    mrpPaise: z.number().int().positive(),
    pricePaise: z.number().int().positive(),
    sku: z.string().trim().max(40).optional(),
    // optional product gallery — 1–4 Cloudinary images
    media: z.array(ProductImportImage).max(4).optional(),
  })
  .strict();

export const BulkImportProductsRequest = z
  .object({
    vendorId: Id,
    products: z.array(ProductImportRow).min(1).max(500),
  })
  .strict();
export type BulkImportProductsRequest = z.infer<typeof BulkImportProductsRequest>;

export const BulkExportQuery = z
  .object({
    status: z.string().optional(),
    categoryId: Id.optional(),
    vendorId: Id.optional(),
    productKind: ProductKindSchema.optional(),
  })
  .strict();
export type BulkExportQuery = z.infer<typeof BulkExportQuery>;
export * from './vendorBulk';
